import React, { useEffect, useState, useRef, useMemo } from 'react'
import { ClearSocketMessageHandler, ConnectWebSocketForOverlay, DisconnectWebSocket, SetSocketMessageHandler } from './../../../utilities/BL/Zones'
import { GetMachineConfig as BLGetMachineConfig } from './../../../utilities/BL/SlotMachine' // <-- NUEVA IMPORTACIÓN

const TOTAL_SYMBOLS_STRIP = 60;
const SYMBOL_HEIGHT = 240 //300;
const SYMBOL_IMAGES = ['1.png', '2.png', '3.png', '4.png', '5.png', '6.png'];
// const SYMBOL_IMAGES = ['1.png', '19.png', '20.png', '21.png', '22.png', '23.png', '24.png', '25.png', '26.png', '17.png', '18.png'];
//const SYMBOL_IMAGES = ['1.png', '10.png', '11.png', '12.png', '13.png', '14.png', '15.png', '16.png'];
//const SYMBOL_IMAGES = ['1.png', '3.png', '4.png', '5.png', '7.png', '8.png', '9.png'];
//const SYMBOL_IMAGES = ['1.png','6.png','7.png','8.png','9.png','10.png','11.png','12.png','13.png','14.png','15.png','16.png','17.png','18.png','19.png']; 

/**
 * COMPONENTE: SLOT MACHINE (PANTALLA GIGANTE)
*/
const SlotMachine = ({ screenId = 1, isStandalone = true, payload = null }) => {
    const [connectionStatus, setConnectionStatus] = useState('idle');
    const [statusMessage, setStatusMessage] = useState('LISTO');
    const [machineState, setMachineState] = useState('idle'); 

    const [showQR, setShowQR] = useState(false);
    const [showThanks, setShowThanks] = useState(false);
    
    // --- NUEVOS ESTADOS ---
    const [machineScale, setMachineScale] = useState(100);
    const [identifyData, setIdentifyData] = useState(null);

    const machineMainRef = useRef(null);
    const lcdDisplayRef = useRef(null);
    const flashOverlayRef = useRef(null);
    const leverRodRef = useRef(null);
    
    const stripRefs = [useRef(null), useRef(null), useRef(null)];
    const lightsIntervalRef = useRef(null);

    // --- OBTENER CONFIGURACIÓN INICIAL DE TAMAÑO ---
    const fetchMachineConfig = async () => {
        const res = await BLGetMachineConfig(screenId);
        if (res && res.success && res.data) {
            setMachineScale(res.data.scale || 100);
        }
    };

    // --- EFECTO 1: Conexión WebSocket ---
    useEffect(() => {
        fetchMachineConfig(); // Cargar tamaño al montar componente

        if (!isStandalone) {
            startIdleLights('gold');
            return;
        }

        const connectToWebSocket = async () => {
            const overlayType = `slotmachine:screen:${screenId}`;
            await ConnectWebSocketForOverlay(
                overlayType,
                (status) => setConnectionStatus(status),
                (toastMsg) => console.log(`[SlotMachine WS] ${toastMsg}`)
            );
            SetSocketMessageHandler(handleIncomingMessage);
        };

        connectToWebSocket();
        startIdleLights('gold');

        return () => {
            if (lightsIntervalRef.current) clearInterval(lightsIntervalRef.current);
            ClearSocketMessageHandler();
            DisconnectWebSocket();
        };
    }, [screenId, isStandalone]);

    // --- EFECTO 2: Escuchar las órdenes del Padre (Screen.js) ---
    useEffect(() => {
        if (!isStandalone && payload) {
            handleIncomingMessage(payload);
        }
    }, [payload, isStandalone]);

    const handleIncomingMessage = (message) => {
        if (!message || !message.type) return;

        const type = message.type;
        const msgData = message.data || message.payload || {};

        switch (type.toLowerCase()) {
            case 'slotmachine.prepare':
                fetchMachineConfig(); // Vuelve a consultar el tamaño al reiniciar por si el Admin lo cambió
                resetMachine();
                setStatusMessage('LISTO');
                setShowQR(false);
                setShowThanks(false);
                break;

            case 'slotmachine.spin':
                executeSpinAnimation(msgData);
                setShowQR(false); 
                break;

            case 'slotmachine.toggleqr':
                setShowQR(msgData.isVisible);
                break;

            case 'slotmachine.togglethanks':
                setShowThanks(msgData.isVisible);
                break;
                
            // NUEVO CASO: Escuchar petición de identificación
            case 'slotmachine.identify':
                setIdentifyData({ show: true, name: msgData.screenName || `Pantalla #${screenId}` });
                setTimeout(() => setIdentifyData(null), 3000); // Se oculta sola a los 3 segundos
                break;

            default:
                break;
        }
    };

    // -------------------------------------------------------------------------
    // LÓGICA DE ANIMACIÓN CON CLASES BEM
    // -------------------------------------------------------------------------
    const startIdleLights = (colorTheme = 'gold') => {
        if (lightsIntervalRef.current) clearInterval(lightsIntervalRef.current);
        const bulbs = document.querySelectorAll('.slot-machine__bulb');
        bulbs.forEach(bulb => {
            bulb.className = `slot-machine__bulb slot-machine__bulb--on ${colorTheme === 'red' ? 'slot-machine__bulb--red' : ''}`;
            bulb.style.animation = `bulbIdle${colorTheme.charAt(0).toUpperCase() + colorTheme.slice(1)} 3s ease-in-out infinite`;
            bulb.style.animationDelay = `${Math.random() * 2}s`;
        });
    };

    const startSpinningLights = () => {
        if (lightsIntervalRef.current) clearInterval(lightsIntervalRef.current);
        const bulbs = document.querySelectorAll('.slot-machine__bulb');
        let currentStep = 0;
        lightsIntervalRef.current = setInterval(() => {
            bulbs.forEach((bulb, index) => {
                bulb.className = 'slot-machine__bulb'; 
                bulb.style.animation = 'none';
                if ((index + currentStep) % 4 === 0) {
                    bulb.classList.add('slot-machine__bulb--on');
                }
            });
            currentStep++;
        }, 80);
    };

    const executeSpinAnimation = async (spinData) => {
        const { isWinner, visualResult, baseTimeMs, celebrationTimeMs } = spinData;

        setMachineState('spinning');
        setStatusMessage('SUERTE...');
        startSpinningLights();
        
        if (leverRodRef.current) {
            leverRodRef.current.classList.add('slot-machine__lever-rod--active');
            setTimeout(() => leverRodRef.current.classList.remove('slot-machine__lever-rod--active'), 500);
        }

        const spinPromises = stripRefs.map((stripRef, index) => {
            return new Promise(resolve => {
                const stripElement = stripRef.current;
                if (!stripElement) { resolve(); return; }

                const targetIndex = TOTAL_SYMBOLS_STRIP - 5;
                if (stripElement.children[targetIndex]) {
                    const imgElement = stripElement.children[targetIndex].querySelector('img');
                    if (imgElement) imgElement.src = `/img/games/slotmachine/${visualResult[index]}`;
                }

                const distanceToScroll = targetIndex * SYMBOL_HEIGHT;
                const spinDuration = baseTimeMs + (index * 800);

                stripElement.style.transition = `transform ${spinDuration}ms cubic-bezier(0.1, 0, 0.1, 1)`;
                stripElement.style.transform = `translateY(-${distanceToScroll}px)`;

                setTimeout(resolve, spinDuration);
            });
        });

        await Promise.all(spinPromises);

        if (isWinner) {
            triggerWinnerCelebration(celebrationTimeMs);
        } else {
            const almostWin = (visualResult[0] === visualResult[1] || visualResult[1] === visualResult[2] || visualResult[0] === visualResult[2]);
            triggerLoserState(almostWin);
        }

        setTimeout(() => silentlyResetReels(visualResult), 1500);
    };

    const triggerWinnerCelebration = (durationMs) => {
        setMachineState('winner');
        setStatusMessage('🥳 ¡GANASTE! 🥳');
        
        if (flashOverlayRef.current) {
            flashOverlayRef.current.classList.add('slot-machine__flash-overlay--active');
            setTimeout(() => flashOverlayRef.current.classList.remove('slot-machine__flash-overlay--active'), 400);
        }

        const celebrationEnd = Date.now() + durationMs;
        const confettiInterval = setInterval(() => {
            if (Date.now() > celebrationEnd) {
                clearInterval(confettiInterval);
                return;
            }
            createConfettiExplosion();
        }, 300);

        setTimeout(() => resetMachine(), durationMs);
    };

    const triggerLoserState = (wasClose) => {
        setMachineState('loser');
        setStatusMessage(wasClose ? '😧 CASI 😧' : '😔 FALLASTE 😔');
        startIdleLights('red');
        setTimeout(() => resetMachine(), 2500);
    };

    const resetMachine = () => {
        setMachineState('idle');
        setStatusMessage('LISTO');
        startIdleLights('gold');
    };

    const silentlyResetReels = (lastResult) => {
        stripRefs.forEach((stripRef, i) => {
            const strip = stripRef.current;
            if (!strip) return;
            strip.style.transition = 'none';
            strip.style.transform = 'translateY(0px)';
            Array.from(strip.children).forEach((child, idx) => {
                const img = child.querySelector('img');
                if (img) {
                    img.src = idx === 0 
                        ? `/img/games/slotmachine/${lastResult[i]}` 
                        : `/img/games/slotmachine/${SYMBOL_IMAGES[Math.floor(Math.random() * SYMBOL_IMAGES.length)]}`;
                }
            });
        });
    };

    const createConfettiExplosion = () => {
        for(let i = 0; i < 40; i++) {
            const confetti = document.createElement('div');
            confetti.className = 'slot-confetti';
            confetti.style.left = Math.random() * 100 + 'vw';
            confetti.style.backgroundColor = `hsl(${Math.random() * 360}, 100%, 50%)`;
            document.body.appendChild(confetti);
            
            confetti.animate([
                { transform: 'translateY(0) rotate(0)', opacity: 1 }, 
                { transform: `translateY(110vh) rotate(${Math.random() * 1500}deg) translateX(${(Math.random() - 0.5) * 300}px)`, opacity: 0 }
            ], { 
                duration: 3000 + Math.random() * 2000, 
                easing: 'cubic-bezier(0, .5, .5, 1)' 
            }).onfinish = () => confetti.remove();
        }
    };

    // -------------------------------------------------------------------------
    // RENDERIZADO INICIAL CON USEMEMO
    // -------------------------------------------------------------------------
    
    const perimeterLightsJsx = useMemo(() => {
        let lights = [];
        for(let i=0; i<40; i++) {
            lights.push(<div key={i} className="slot-machine__bulb slot-machine__bulb--on"></div>);
        }
        return lights;
    }, []);

    const reelsJsx = useMemo(() => {
        return [0, 1, 2].map(reelIdx => {
            let items = [];
            for (let i = 0; i < TOTAL_SYMBOLS_STRIP; i++) {
                const randomImg = SYMBOL_IMAGES[Math.floor(Math.random() * SYMBOL_IMAGES.length)];
                items.push(
                    <div key={i} className="slot-machine__symbol">
                        <img src={`/img/games/slotmachine/${randomImg}`} alt="symbol" />
                    </div>
                );
            }
            return items;
        });
    }, []);


    return (
        <>
            <div className='SelfieFan__Screen__QR__Background'>
                {/* <video 
                    src='/img/page/backgrounds/Fondo-SelfieFan-Varonil-Cadenas-Zoom-Master-2.mp4' 
                    className='SelfieFan__Screen__QR__Background__Video' 
                    autoPlay loop muted playsInline 
                /> */}

                {/* <img src='/img/page/backgrounds/background-womens-day-2026.png' className='SelfieFan__Screen__Selfie__Background__Image' /> */}
                {/* <img src='/img/games/slotmachine/Fondo-SelfieFan-Varonil.png' className='SelfieFan__Screen__Selfie__Background__Image' /> */}
                <img src='/img/page/backgrounds/background.png' className='SelfieFan__Screen__Selfie__Background__Image' />
            </div>

            <div className={`slot-machine ${machineState === 'winner' ? 'slot-machine--winner' : ''} ${machineState === 'loser' ? 'slot-machine--loser' : ''}`}>
                <div className="slot-machine__video-overlay"></div>
                <div ref={flashOverlayRef} className="slot-machine__flash-overlay"></div>

                {/* OVERLAY DE AGRADECIMIENTO */}
                {showThanks && (
                    <>
                        <div className="slot-machine__thanks-overlay">
                            <img src="/img/page/adsl-five.png" alt="Logo Adsl Five" className='d-block mb-4'/>
                            <h1 className="thanks-title">¡GRACIAS POR TU PARTICIPACIÓN!</h1>
                            {/* <p className="thanks-subtitle">Sigue disfrutando del evento</p> */}
                        </div>

                        <footer className='slot-machine__Footer--V2'>Hecho con ♥ por Interaktia | 2026</footer>
                    </>
                )}

                {/* OVERLAY DE QR */}
                {showQR && !showThanks && (
                    <div className="slot-machine__qr-overlay">
                        <div className="qr-card">
                            <p className="qr-title">¡ESCANEA Y JUEGA!</p>
                            <img src="/img/games/slotmachine/qr-code.png" alt="Código QR" className="qr-image" />
                        </div>
                    </div>
                )}

                {/* OVERLAY DE IDENTIFICACIÓN DE PANTALLA */}
                {identifyData && identifyData.show && (
                    <div style={{
                        position: 'fixed', bottom: '30px', left: '30px',
                        backgroundColor: 'rgba(0, 0, 0, 0.9)', color: '#fff',
                        padding: '6px 6px', borderRadius: '15px', zIndex: 9999,
                        border: '4px solid #dc2626', fontSize: '20px', fontWeight: 'bold',
                        boxShadow: '0 0 30px rgba(#fff, 0.1)'
                    }}>
                        <p className='mb-0 text-center'><i className="bi bi-tv"></i></p>
                        <p className='mb-0'>{identifyData.name}</p>
                    </div>
                )}

                {/* AQUÍ APLICAMOS LA MAGIA DEL ZOOM CSS */}
                <div 
                    className={`slot-machine__content ${showThanks ? 'd-none' : ''}`}
                    style={{ 
                        transform: `scale(${machineScale / 100})`, 
                        transformOrigin: 'center center',
                        transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)' 
                    }}
                >
                    {/* <div className="slot-machine__header">
                        <div className="slot-machine__logo-box">
                            <img src="/img/page/adsl-five.png" alt="Logo ADSL Five" />
                        </div>
                    </div> */}

                    <div ref={machineMainRef} className="slot-machine__frame">
                        <img src="/img/page/adsl-five.png" alt="Logo Adsl Five" style={{ width: '100%', maxWidth: '450px', margin: '0 auto', display: 'block', marginBottom: '12px' }} />

                        <div className="slot-machine__lights">
                            {perimeterLightsJsx}
                        </div>

                        <div className="slot-machine__panel">
                            <div className="slot-machine__viewport">
                                <div className="slot-machine__reel"><div className="slot-machine__strip" ref={stripRefs[0]}>{reelsJsx[0]}</div></div>
                                <div className="slot-machine__reel"><div className="slot-machine__strip" ref={stripRefs[1]}>{reelsJsx[1]}</div></div>
                                <div className="slot-machine__reel"><div className="slot-machine__strip" ref={stripRefs[2]}>{reelsJsx[2]}</div></div>
                            </div>
                        </div>

                        <div ref={lcdDisplayRef} className={`slot-machine__display slot-machine__display--${machineState}`}>
                            <div className={`slot-machine__status-text ${machineState === 'idle' ? 'slot-machine__status-text--breathing' : `slot-machine__status-text--${machineState}`}`}>
                                {statusMessage}
                            </div>
                        </div>

                        <div className="slot-machine__lever">
                            <div className="slot-machine__lever-rod" ref={leverRodRef}>
                                <div className="slot-machine__lever-ball"></div>
                            </div>
                        </div>
                    </div>
                </div>

                <footer className='slot-machine__Footer' style={{ color: '#000000', backgroundColor: 'rgba(255,255,255,0.8)', padding: '0 16px', borderRadius: '16px', fontSize: '24px' }}>Hecho con ♥ por Interaktia | 2026</footer>
                
                {connectionStatus === 'error' && (
                    <div className="ws-error-badge" style={{position: 'absolute', bottom: '10px', color: 'white', background: 'red', padding: '5px 10px', borderRadius: '5px'}}>⚠️ Desconectado</div>
                )}
            </div>
            
        </>
    );
};

export { SlotMachine };